/**
 * 認証ユーザーの確認（supabase.auth.getUser）を、画面・ガードの間で共有する（指示書 A3・④⑨⑩）。
 *
 * - 実行中の確認があれば、その Promise を共有する（同時に呼んでも通信は1回）。
 * - 確認できたユーザーは、短い時間（既定 10秒）だけ再利用する。一遷移と、その直後の画面の読み込みを
 *   1回の確認で済ませるため。無期限には持たない。
 * - 失敗（通信エラー・例外）とセッション無しは再利用しない。次の呼び出しで確認し直す。
 * - ログアウト・ユーザー情報の更新で破棄する（invalidate）。破棄より前に始まった確認の結果は、
 *   保存せず、待っていた呼び出し元にも渡さない（今の状態で確認し直す）。
 * - 認証イベントで分かった「今のユーザー」と違うユーザーの結果は、保存せず、呼び出し元にも渡さない。
 *   確認を始めた後にログインの通知があったときは、その前の「セッション無し」・認証エラーも渡さない。
 *   同じユーザーのログイン通知（ページを開いた直後のセッション復元など）では、実行中の確認をそのまま使う。
 * - 古い結果のときの確認し直しは1回まで。それでも状態が変わり続けたら、古い結果ではなく
 *   回復できるエラー（AUTH_STATE_CHANGED_ERROR。画面は「通信の失敗」と同じく再試行を案内する）を返す。
 * - 実行中の確認が長く終わらない（既定 8秒）ときは、新しい確認を始める（古い確認を待ち続けない）。
 *
 * これは画面側の重複確認を減らすためのもの。API・DB の権限確認（JWT・RLS）は、これまでどおりサーバーが毎回行う。
 */

export const AUTH_USER_REUSE_MS = 10000
export const AUTH_USER_STALE_IN_FLIGHT_MS = 8000

/** 確認の途中で認証状態が変わり続けたときのエラー。未ログインとは扱わない（classifyGetUserResult では 'error'） */
export const AUTH_STATE_CHANGED_ERROR = Object.freeze({
  name: 'AuthStateChangedError',
  status: 0,
  message: 'auth state changed while checking the user'
})
const stateChangedResult = () => ({ data: { user: null }, error: AUTH_STATE_CHANGED_ERROR })

/**
 * @param {{
 *   fetchUser: () => Promise<{ data?: { user?: object|null }, error?: any }>,
 *   now?: () => number,
 *   reuseMs?: number,
 *   staleInFlightMs?: number
 * }} deps
 */
export function createSharedAuthUser({
  fetchUser,
  now = () => Date.now(),
  reuseMs = AUTH_USER_REUSE_MS,
  staleInFlightMs = AUTH_USER_STALE_IN_FLIGHT_MS
}) {
  let generation = 0
  let requestSeq = 0
  /** @type {{ promise: Promise<any>, startedAt: number, generation: number, snap: { generation: number, knownAt: any } } | null} */
  let inFlight = null
  /** @type {{ result: any, userId: string, at: number } | null} */
  let confirmed = null
  // 認証イベントで分かった今のユーザー（undefined: まだ分からない / null: ログアウト）
  let knownUserId

  const userIdOf = (result) => (!result?.error && result?.data?.user?.id) || null

  // 確認を始めた時点（snap）から認証状態が変わり、結果が今の状態と合わないか
  //  - ログアウト・ユーザー情報の更新があった（generation が変わった）
  //  - 結果のユーザーが、今のユーザーと違う（前のユーザー・ログアウト後に届いたユーザー）
  //  - 確認を始めた後にログインの通知があり、結果がユーザー無し（以前の「セッション無し」・認証エラー）
  const isStale = (result, snap) => {
    if (snap.generation !== generation) return true
    const userId = userIdOf(result)
    if (userId) return knownUserId !== undefined && userId !== knownUserId
    return Boolean(knownUserId) && snap.knownAt !== knownUserId
  }

  const remember = (result, snap, seq) => {
    // 後から始まった確認がある古い確認・状態が変わった後の結果は、保存しない
    if (seq !== requestSeq || isStale(result, snap)) return
    const userId = userIdOf(result)
    confirmed = userId ? { result, userId, at: now() } : null
  }

  // 呼び出し元へ渡す時点でも、認証状態が変わっていないか確かめる。
  // 古い結果は渡さず、今の状態で確認し直す（1回だけ）。それでも古ければ、回復できるエラーを返す
  const deliver = (raw, snap, retried) => raw.then(
    (result) => {
      if (!isStale(result, snap)) return result
      return retried ? stateChangedResult() : request(true)
    },
    (err) => {
      if (!isStale({ data: { user: null }, error: err }, snap)) throw err
      return retried ? stateChangedResult() : request(true)
    }
  )

  const snapshot = () => ({ generation, knownAt: knownUserId })

  function get() {
    return request(false)
  }

  function request(retried) {
    const t = now()
    if (confirmed && t - confirmed.at < reuseMs) return deliver(Promise.resolve(confirmed.result), snapshot(), retried)
    if (inFlight && inFlight.generation === generation && t - inFlight.startedAt < staleInFlightMs) {
      return deliver(inFlight.promise, inFlight.snap, retried)
    }
    const snap = snapshot()
    const seq = ++requestSeq
    let promise
    try {
      promise = Promise.resolve(fetchUser())
    } catch (e) {
      promise = Promise.reject(e)
    }
    const entry = { promise, startedAt: t, generation: snap.generation, snap }
    inFlight = entry
    promise.then(
      (result) => {
        remember(result, snap, seq)
        if (inFlight === entry) inFlight = null
      },
      () => {
        if (inFlight === entry) inFlight = null
      }
    )
    return deliver(promise, snap, retried)
  }

  /** ログアウト・ユーザー切り替えなど。確認済みの結果と、実行中の確認の共有をやめる */
  function invalidate() {
    generation += 1
    confirmed = null
    inFlight = null
  }

  /**
   * 実行中の確認の共有をやめる（止まった確認を、次の呼び出しで待ち直さない）。認証状態は変えない。
   * すでに待っている呼び出し元には、その確認の結果がそのまま届く
   */
  function abandonInFlight() {
    inFlight = null
  }

  /** サーバーから受け取ったばかりのユーザー（ログイン成功の応答）を、確認済みとして登録する */
  function seed(user) {
    if (!user?.id) return
    invalidate()
    knownUserId = user.id
    confirmed = { result: { data: { user }, error: null }, userId: user.id, at: now() }
  }

  /** 認証イベントを受けて、必要なら破棄する。同じユーザーのログイン通知・トークン更新では破棄しない */
  function handleAuthEvent(event, session) {
    const nextUserId = session?.user?.id || null
    if (event === 'SIGNED_OUT' || !nextUserId) {
      invalidate()
      knownUserId = null
      return
    }
    knownUserId = nextUserId
    if (event === 'USER_UPDATED') {
      invalidate()
      return
    }
    // 別のユーザーになった: 確認済みの結果は捨てる。実行中の確認は、届いた時点でユーザーを確かめる（違えば渡さない）
    if (confirmed && confirmed.userId !== nextUserId) confirmed = null
  }

  return {
    get,
    invalidate,
    abandonInFlight,
    seed,
    handleAuthEvent,
    /** テスト・観測用 */
    get confirmedUserId() { return confirmed?.userId || null }
  }
}
